/**
 * One sentence about the plan as a whole, above the canvas.
 *
 * ONE of them, since 16 September 2026: the one-time note that says what editing a running plan does
 * (mobile's `LivePlanNote`). It states a fact about the plan rather than about anything a trainer
 * just did, so it is not an alert and it does not interrupt.
 *
 * The second caller was the reason the phase in view takes no new training, and it went into the
 * refused COLUMN (owner's decision, over the UX review's own alternative: the reason belongs at the
 * control). That is what actually removed the 31.3px the canvas moved on a phase switch — a row that
 * appears and disappears moves the canvas whether or not it was mounted all along, and the only
 * other cures were a permanently reserved empty band or an overlay. The live note's own dismissal
 * still gives its row back, and that is deliberate: it is a one-time act the trainer asked for.
 *
 * It is `status`, not `alert`, and it carries a `role` at all only because a note appearing after a
 * phase switch would otherwise reach nobody.
 *
 * The dismiss is optional and exactly one caller has it. A note with no way out is a note that
 * belongs on the screen for as long as the fact is true, which is the refusal's case.
 *
 * ── The row is ALWAYS mounted and the SENTENCE is what changes ───────────────────────────────
 * Both notes used to mount with their text in them, and that broke the same two rules the overflow
 * banner above the canvas already answers (`WeekCanvas`, `PlanBuilderScreen.module.css`):
 *
 * · **the canvas moved.** A phase switch onto a closed phase inserted this row and pushed the week
 *   down 31.3px, measured; dismissing the live note took the same 31.3px back out.
 * · **the note was never announced.** A `role="status"` inserted TOGETHER with its content is not
 *   reliably announced — the region has to be in the accessibility tree before the text changes.
 *
 * So the row stands whether or not there is anything to say, and an empty one is STRIPPED rather
 * than `display: none`: none would take the region back out of the tree, which is the defect again.
 * An empty row is a 0px box with no padding and no border, and it draws nothing.
 */

import styles from './BuilderNote.module.css';

interface BuilderNoteProps {
  /** The sentence, or `''` while there is nothing to say. The row stands either way. */
  readonly message: string;
  /** Present only on the one-time live note. Omitted, the note carries no control at all. */
  readonly dismiss?: { readonly label: string; readonly onDismiss: () => void };
}

export function BuilderNote({ message, dismiss }: BuilderNoteProps) {
  const isEmpty = message === '';
  return (
    <div
      className={`${String(styles.note)} ${isEmpty ? String(styles.noteEmpty) : ''}`}
      role="status"
    >
      <p className={styles.message}>
        {message}
      </p>
      {/* A note with no sentence has nothing to dismiss. Decided here rather than at the call site,
        * so the two conditions cannot drift apart. */}
      {dismiss !== undefined && !isEmpty && (
        <button
          type="button"
          className={styles.dismiss}
          aria-label={dismiss.label}
          onClick={dismiss.onDismiss}
        >
          <span aria-hidden="true">×</span>
        </button>
      )}
    </div>
  );
}
